import React, { useCallback, useEffect, useMemo } from 'react'
import RadioGroup from '../../../atoms/form/input/RadioGroup'
import VerticalGroup from '../../../atoms/verticalgroup/VerticalGroup'
import useForm from '../../../../hooks/useForm'
import IntegerDropdown from '../../../organisms/integer-dropdown/IntegerDropdown'
import DisplayField from '../../../atoms/form/display-field/DisplayField'
import InlineGroup from '../../../atoms/inlinegroup/InlineGroup'
import Datepicker from '../../../atoms/datepicker/Datepicker'
import Form from '../../../atoms/form/Form'
import Text from '../../../atoms/text/Text'

import '../../../atoms/form/input/Input.scss'
import Button from '../../../atoms/button/Button'
import { addDaysToDate, duration, HOUR_MILLISECONDS } from '../../../../lib/date'
import InformationPopup from '../../../organisms/information-popup/InformationPopup'
import CancelButton from '../../../atoms/form/actions/CancelButton'
import SimpleModal from '../../../organisms/standard-modal/SimpleModal'
import { pluralise } from '../../../../lib/pluralise'

const DEFAULT_MINS_ALLOCATED = 45
interface RoundSchedule {
  timeAllocated: number
  startTime: Date | null
  finishTime: Date | null
}
interface RoundsScheduleProps {
  label: string
  facilitatedRoundsEnabled: boolean
  schedule: RoundSchedule
  onChange: (value: RoundSchedule) => void
  fieldErrors: {
    startTime?: string
    finishTime?: string
  }
}

const RoundSchedulingRow: React.FC<RoundsScheduleProps> = ({ label, facilitatedRoundsEnabled, schedule, fieldErrors, onChange }) => {
  if (facilitatedRoundsEnabled) {
    return <IntegerDropdown
      isCreatable
      value={schedule.timeAllocated}
      onChange={(selectedValue: { value: string, label: string }) => onChange({ ...schedule, timeAllocated: parseInt(selectedValue?.value ?? 5) })}
      min={5}
      max={90}
      step={5}
      labelFormatter={(val: number) => `${val} mins`}
      label={label}
    />
  }
  return <DisplayField stretch label={label} verticalCenter value={
    <InlineGroup spaceLeft={2}>
      <Datepicker
        date={schedule.startTime}
        onChange={(value: Date | null) => onChange({ ...schedule, startTime: value })}
        withTime
        error={fieldErrors.startTime}
      />
      <Datepicker
        date={schedule.finishTime}
        onChange={(value: Date | null) => onChange({ ...schedule, finishTime: value })}
        withTime
        error={fieldErrors.finishTime}
      />
      <div className='display-field-value'>
        <Text size='sm' className='ml-2'>{duration(schedule.startTime!, schedule.finishTime!)}</Text>
      </div>
    </InlineGroup>
  } />
}

interface Props {
  isOpen: boolean
  onClose: () => void
  schedule?: API.EventSchedule
  simulation?: API.SimulationResponse
  onSave: (value: API.EventSchedule) => void
  onCancel: () => void
}

const getDefaultDate = (): Date => {
  const now = new Date()
  now.setSeconds(0)
  now.setMilliseconds(0)
  const nowMins = now.getMinutes()
  if (nowMins === 0 || nowMins === 30) {
    return now
  }
  if (nowMins < 30) {
    now.setMinutes(30)
    return now
  }

  // add an hour
  now.setMinutes(0)
  now.setTime(now.getTime() + (HOUR_MILLISECONDS))
  return now
}

const formatDateForAPI = (value: Date | null) => {
  value = value ?? getDefaultDate()
  value.setSeconds(0)
  value.setMilliseconds(0)
  return value.toISOString()
}

const setStateFromSchedule = (schedule: API.EventSchedule, simulation: API.SimulationResponse) => ({
  facilitatedRoundsEnabled: schedule.roundType === 'facilitated',
  scheduledRoundsEnabled: schedule.roundType === 'scheduled',
  rounds: simulation.generalSetup.rounds,
  daysBetweenRounds: 7,
  schedules: schedule.schedules.map((roundSchedule: API.RoundSchedule) => ({
    timeAllocated: roundSchedule.timeAllocated || DEFAULT_MINS_ALLOCATED,
    startTime: roundSchedule.startTime ? new Date(roundSchedule.startTime) : getDefaultDate(),
    finishTime: roundSchedule.finishTime ? new Date(roundSchedule.finishTime) : getDefaultDate()
  }))
})

const EventScheduling: React.FC<Props> = ({ schedule, simulation, onSave, onCancel, isOpen, onClose }) => {
  const [{
    formData,
    fieldErrors
  }, { setRadioField, setFieldRaw, setDropdownField, setError }] = useForm(schedule && simulation ? setStateFromSchedule(schedule, simulation) : {
    facilitatedRoundsEnabled: true,
    scheduledRoundsEnabled: false,
    rounds: simulation?.generalSetup.rounds || 3,
    daysBetweenRounds: 7,
    schedules: new Array(4).fill({
      timeAllocated: DEFAULT_MINS_ALLOCATED,
      startTime: getDefaultDate(),
      finishTime: getDefaultDate()
    })
  })

  const {
    facilitatedRoundsEnabled,
    scheduledRoundsEnabled,
    rounds,
    daysBetweenRounds,
    schedules,
  } = formData

  const validate = useCallback((updatedSchedules) => {
    const errors = []

    if (scheduledRoundsEnabled) {

      for (let i = 0; i < updatedSchedules.length; i++) {
        const schedule = updatedSchedules[i]
        if (i === 0) {
          if (schedule.startTime < new Date()) {
            errors.push({ field: `schedule-${i}-startTime`, message: 'Can\'t be in the past' })
          } else {
            errors.push({ field: `schedule-${i}-startTime`, message: undefined })
          }
        }
        if (i !== 0) {
          const previousRound = updatedSchedules[i - 1]
          if (previousRound.finishTime > schedule.startTime) {
            errors.push({ field: `schedule-${i}-startTime`, message: 'Can\'t be before finish time of previous round' })
          } else {
            errors.push({ field: `schedule-${i}-startTime`, message: undefined })
          }
        }
        if (schedule.finishTime <= schedule.startTime) {
          errors.push({ field: `schedule-${i}-finishTime`, message: 'Finish time must be after start time' })
        } else {
          errors.push({ field: `schedule-${i}-finishTime`, message: undefined })
        }
      }
    }
    return errors
  }, [scheduledRoundsEnabled])

  const onSubmit = useCallback(() => {
    const errors = validate(schedules)
    for (const err of errors) {
      setError(err.field, err.message)
    }
    if (errors.filter(e => e.message !== undefined).length) {
      return
    }

    onSave({
      roundType: facilitatedRoundsEnabled ? 'facilitated' : 'scheduled',
      schedules: schedules.map((s: RoundSchedule) => ({
        timeAllocated: facilitatedRoundsEnabled ? s.timeAllocated : 0,
        startTime: facilitatedRoundsEnabled ? null : formatDateForAPI(s.startTime),
        finishTime: facilitatedRoundsEnabled ? null : formatDateForAPI(s.finishTime)
      }))
    })
  }, [onSave, schedules, facilitatedRoundsEnabled, setError, validate])

  const onRadioChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setRadioField(['facilitatedRoundsEnabled', 'scheduledRoundsEnabled'])(e.target.value)
  }, [setRadioField])

  const radioGroups = useMemo(() => {
    return [{
      label: 'Facilitated Rounds',
      value: 'facilitatedRoundsEnabled',
      checked: facilitatedRoundsEnabled
    }, {
      label: 'Specific Start/End Dates',
      value: 'scheduledRoundsEnabled',
      checked: scheduledRoundsEnabled
    }]
  }, [facilitatedRoundsEnabled, scheduledRoundsEnabled])

  useEffect(() => {
    const roundsPlusWrapUp = rounds + 1
    if (roundsPlusWrapUp !== schedules.length) {
      if (roundsPlusWrapUp > schedules.length) {
        const remaining = roundsPlusWrapUp - schedules.length
        return setFieldRaw('schedules', schedules.concat(new Array(remaining).fill({
          timeAllocated: DEFAULT_MINS_ALLOCATED,
          startTime: getDefaultDate(),
          finishTime: getDefaultDate()
        })))
      } else {
        return setFieldRaw('schedules', schedules.slice(0, roundsPlusWrapUp))
      }
    }
  }, [rounds, schedules, setFieldRaw])

  const generateScheduledRounds = useCallback(() => {
    const startingRound = schedules[0]
    const generatedSchedule = schedules.map((s: RoundSchedule, i: number) => {
      if (i === 0) {
        return s
      }
      return {
        ...s,
        startTime: addDaysToDate(i * daysBetweenRounds, startingRound.startTime),
        finishTime: addDaysToDate(i * daysBetweenRounds, startingRound.finishTime)
      }
    })
    setFieldRaw('schedules', generatedSchedule)
    const errors = validate(generatedSchedule)
    for (const err of errors) {
      setError(err.field, err.message)
    }

  }, [schedules, daysBetweenRounds, setFieldRaw, validate, setError])

  const updateRoundSchedule = useCallback((index: number) => {
    return (value: RoundSchedule) => {
      const updatedSchedules = schedules.map((s: RoundSchedule, scheduleIndex: number) => {
        if (scheduleIndex === index) {
          return value
        }
        return s
      })
      setFieldRaw('schedules', updatedSchedules)
      const errors = validate(updatedSchedules)
      for (const err of errors) {
        setError(err.field, err.message)
      }
    }
  }, [schedules, setFieldRaw, setError, validate])

  return <SimpleModal
    size='large'
    isOpen={isOpen}
    onClose={onClose}
    isScrollable
  >
    <VerticalGroup wide spaceBetweenElements={4}>
      <h2>Round Scheduling</h2>
      <Form wide>
        <InlineGroup spaceLeft={2}>
          <RadioGroup
            name='round-style'
            label='Round Style'
            onChange={onRadioChange}
            fields={radioGroups}
          />
          <InformationPopup
            shadow
            title="Round Style"
            body={(
              <>
                <p>Facilitated Rounds – These rounds will be manually started by facilitators during an event. </p>
                <p>Scheduled Rounds – These rounds will automatically start and stop based on the schedule below.</p>
              </>
            )}
          />
        </InlineGroup>
        {scheduledRoundsEnabled &&
          <InlineGroup spaceLeft={2} verticalCenter>
            <IntegerDropdown
              label='Repeat every'
              value={daysBetweenRounds}
              onChange={setDropdownField('daysBetweenRounds')}
              min={1}
              max={31}
              labelFormatter={(val: number) => `${val} ${pluralise('day', val)}`}
            />
            <Button wide onClick={generateScheduledRounds}>Create Schedule</Button>
            <InformationPopup
              shadow
              title="How to create a schedule"
              body={(
                <>
                  <p>Step 1: Select Round 1 start date &#38; time</p>
                  <p>Step 2: Select Round 1 end date &#38; time</p>
                  <p>Step 3: Select “Repeat every X days”</p>
                  <p>Step 4: Click the “Create Schedule” button</p>
                </>
              )}
            />
          </InlineGroup>}
        {scheduledRoundsEnabled && <DisplayField label='' value={
          <InlineGroup spaceLeft={2}>
            <div className='input-width'>
              <Text size='sm' className='input-width' bold>Starting Date and Time</Text>
            </div>
            <div className='input-width'>
              <Text size='sm' className='input-width' bold>Finishing Date and Time</Text>
            </div>
            <div className='input-width'>
              <Text size='sm' className='input-width' bold>Round Duration</Text>
            </div>
          </InlineGroup>
        } />}
        {schedules.map((schedule: RoundSchedule, i: number) => (
          <RoundSchedulingRow
            key={i}
            label={i < rounds ? `Round ${i + 1}` : 'WrapUp Round'}
            schedule={schedule}
            facilitatedRoundsEnabled={facilitatedRoundsEnabled}
            fieldErrors={{
              startTime: fieldErrors[`schedule-${i}-startTime`],
              finishTime: fieldErrors[`schedule-${i}-finishTime`]
            }}
            onChange={updateRoundSchedule(i)}
          />
        ))}
        <InlineGroup block spread className='mt-4'>
          <CancelButton
            confirmModalProps={{
              confirmButtonText: 'Save',
              inProgress: formData.inProgress,
              showModal: formData.formUpdated,
              onConfirm: onSubmit,
              onDiscard: onCancel
            }}
            onClick={onCancel}
          />
          <Button
            data-test='save'
            form='event-schedule-form'
            wide
            inProgress={false}
            onClick={onSubmit}
          >Save
          </Button>
        </InlineGroup>
      </Form>
    </VerticalGroup>
  </SimpleModal>
}

export default EventScheduling
